// components/common/SweetAlert/SweetAlert.js
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './SweetAlert.css'; // Import the CSS file

const MySwal = withReactContent(Swal);

class SweetAlertService {

  // --- NEW TOASTER METHOD ---
  static showToast({
    icon = 'success', // Options: 'success', 'error', 'warning', 'info'
    title = 'Success',
    position = 'top-end', // Options: 'top-end', 'bottom-end', 'top-center'
    timer = 3000
  }) {
    const Toast = MySwal.mixin({
      toast: true,
      position: position,
      showConfirmButton: false,
      timer: timer,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
      customClass: {
        container: 'swal-toast-parent',
        popup: 'swal-toast-popup',
        title: 'swal-toast-title'
      }
    });

    return Toast.fire({
      icon: icon,
      title: title
    });
  }
  // ---------------------------

  // Success Alert (like your congratulations modal)
  static showSuccess({
    title = 'Success!',
    message = '',
    subtitle = '',
    confirmButtonText = 'Close',
    onConfirm = null,
    allowClose = true
  }) {
    return MySwal.fire({
      icon: 'success',
      title: `<span class="swal-title-success">${title}</span>`,
      html: `
        <div class="swal-content-wrapper">
          ${subtitle ? `<h3 class="custom-message">${subtitle}</h3>` : ''}
          ${message ? `<p class="custom-message">${message}</p>` : ''}
        </div>
      `,
      confirmButtonText,
      confirmButtonColor: '#FF8E1C',
      // showCloseButton: allowClose,
      showCancelButton: false,
      allowOutsideClick: allowClose,
      allowEscapeKey: allowClose,
      customClass: {
        container: 'swal-toast-success',
        popup: 'swal-popup-success',
        confirmButton: 'swal-button-primary',
        title: 'swal-title-container',
        icon: 'swal-icon-small' // <--- ADDED THIS LINE
      }
    }).then((result) => {
      // Always try to clear prospect token when this modal is closed/dismissed
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('prospect_token');
        }
      } catch (e) { }

      if (result.isConfirmed && onConfirm) {
        onConfirm();
      }
      return result;
    });
  }
  // Congratulations Alert (specific for your N-Code completion)
  static showCongratulations({
    userName = 'User',
    onGoHome = null,
    message = 'You have successfully identified your N Codes & E Codes.'
  }) {
    return MySwal.fire({
      title: '<span class="swal-title-congratulations">Congratulations!</span>',
      html: `
        <div class="swal-congratulations-content">
          <p class="swal-success-message">
            ${message}
          </p>
          <p class="swal-email-message">
            Check your email for your results.
          </p>
        </div>
      `,
      // Hide the Go home button and allow the user to close via the X or by clicking outside.
      showConfirmButton: false,
      confirmButtonColor: '#FF8E1C',
      showCloseButton: true,
      showCancelButton: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      customClass: {
        popup: 'swal-popup-congratulations',
        confirmButton: 'swal-button-home',
        title: 'swal-title-container'
      }
    }).then((result) => {
      // Always try to clear prospect token when this modal is closed/dismissed
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('prospect_token');
        }
      } catch (e) { }

      // If the (now-hidden) confirm button was somehow used, call handler.
      if (result.isConfirmed && onGoHome) {
        onGoHome();
      }

      // If the user dismissed the modal (clicked outside or used the close X), reload the page
      // so the parent page can reflect the completed state as required.
      if (result.isDismissed && typeof window !== 'undefined') {
        try {
          window.location.reload();
        } catch (e) {
          // ignore in non-browser environments
        }
      }

      return result;
    });
  }

  // Congulations Alert (specific for baseline completion)
  static showBaseCongratulations({
    userName = 'User',
    onGoHome = null
    , hideGoHome
  }) {
    // Determine whether to hide the Go home button:
    // - explicit `hideGoHome` param takes precedence
    // - otherwise auto-detect if current route/path/query indicates baselines
    const shouldAutoDetect = typeof hideGoHome === 'undefined';
    let shouldHide = false;
    if (!shouldAutoDetect) {
      shouldHide = !!hideGoHome;
    } else if (typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search || '');
        const isBaselinesQuery = urlParams.get('company_name') === 'baselines';
        const isBaselinesPath = (window.location.pathname || '').includes('/baselines');
        shouldHide = isBaselinesQuery || isBaselinesPath;
      } catch (e) {
        shouldHide = false;
      }
    }

    return MySwal.fire({
      title: '<span class="swal-title-congratulations">Congratulations!</span>',
      html: `
        <div class="swal-congratulations-content">
          <p class="swal-success-message">
            You have successfully identified your baselines.
          </p>
          <p class="swal-email-message">
          Contact your coach for further guidance.
          </p>
        </div>
      `,
      confirmButtonText: shouldHide ? undefined : 'Go home',
      confirmButtonColor: '#FF8E1C',
      showCloseButton: shouldHide, // allow user to close when there's no Go home button
      showCancelButton: false,
      showConfirmButton: !shouldHide,
      allowOutsideClick: shouldHide, // let them click outside to dismiss when Go home is hidden
      allowEscapeKey: shouldHide,
      customClass: {
        popup: 'swal-popup-congratulations',
        confirmButton: 'swal-button-home',
        title: 'swal-title-container'
      }
    }).then((result) => {
      // Always try to clear prospect token when this modal is closed/dismissed
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('prospect_token');
        }
      } catch (e) { }

      // If user clicked the Go home button (visible), call handler if provided.
      if (result.isConfirmed && onGoHome) {
        onGoHome();
      }

      // If Go home button was hidden (prospect/baselines route), refresh page on any dismiss/close.
      if (shouldHide && typeof window !== 'undefined') {
        // reload only when the modal was dismissed/closed (not when confirmed)
        if (result.isDismissed || !result.isConfirmed) {
          try {
            window.location.reload();
          } catch (e) {
            // no-op in non-browser environments
          }
        }
      }

      return result;
    });
  }

  // Error Alert
  static showError({
    title = 'Error!',
    message = 'Something went wrong',
    confirmButtonText = 'OK',
    onConfirm = null
  }) {
    return MySwal.fire({
      icon: 'error',
      title: `<span class="swal-title-error">${title}</span>`,
      html: `
        <div class="swal-content-wrapper">
          ${message ? `<p class="custom-message">${message}</p>` : ''}
        </div>
      `,
      confirmButtonText,
      confirmButtonColor: '#EF4444',
      customClass: {
        container: 'swal-toast-success',
        popup: 'swal-popup-success',
        confirmButton: 'swal-button-primary',
        title: 'swal-title-container',
        icon: 'swal-icon-small' // <--- This class triggers the CSS below
      }
    }).then((result) => {
      if (result.isConfirmed && onConfirm) {
        onConfirm();
      }
      return result;
    });
  }
  // Warning Alert
  static showWarning({
    title = 'Warning!',
    message = '',
    confirmButtonText = 'OK',
    cancelButtonText = 'Cancel',
    showCancel = false,
    onConfirm = null,
    onCancel = null
  }) {
    return MySwal.fire({
      title: `<span class="swal-title-warning">${title}</span>`,
      html: `
        <div class="swal-content-wrapper">
          <p class="swal-message">${message}</p>
        </div>
      `,
      confirmButtonText,
      cancelButtonText,
      showCancelButton: showCancel,
      confirmButtonColor: '#F59E0B',
      cancelButtonColor: '#6B7280',
      customClass: {
        popup: 'swal-popup-warning',
        confirmButton: 'swal-button-warning',
        cancelButton: 'swal-button-cancel',
        title: 'swal-title-container'
      }
    }).then((result) => {
      if (result.isConfirmed && onConfirm) {
        onConfirm();
      } else if (result.isDismissed && onCancel) {
        onCancel();
      }
      return result;
    });
  }

  // Info Alert
  static showInfo({
    title = 'Information',
    message = '',
    confirmButtonText = 'OK',
    onConfirm = null
  }) {
    return MySwal.fire({
      title: `<span class="swal-title-info">${title}</span>`,
      html: `
        <div class="swal-content-wrapper">
          <p class="swal-message">${message}</p>
        </div>
      `,
      confirmButtonText,
      confirmButtonColor: '#3B82F6',
      customClass: {
        popup: 'swal-popup-info',
        confirmButton: 'swal-button-info',
        title: 'swal-title-container'
      }
    }).then((result) => {
      if (result.isConfirmed && onConfirm) {
        onConfirm();
      }
      return result;
    });
  }

  // Confirmation Alert
  static showConfirmation({
    title = 'Are you sure?',
    message = '',
    confirmButtonText = 'Yes',
    cancelButtonText = 'No',
    onConfirm = null,
    onCancel = null
  }) {
    return MySwal.fire({
      title: `<span class="swal-title-confirm">${title}</span>`,
      html: `
        <div class="swal-content-wrapper">
          <p class="swal-message">${message}</p>
        </div>
      `,
      confirmButtonText,
      cancelButtonText,
      showCancelButton: true,
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#EF4444',
      customClass: {
        popup: 'swal-popup-confirm',
        confirmButton: 'swal-button-confirm',
        cancelButton: 'swal-button-cancel',
        title: 'swal-title-container'
      }
    }).then((result) => {
      if (result.isConfirmed && onConfirm) {
        onConfirm();
      } else if (result.isDismissed && onCancel) {
        onCancel();
      }
      return result;
    });
  }
}

export default SweetAlertService;